import { PREFIX_CLS, THEME_COLOR_LIST } from '../../../common/constants';
import { MsgDataType } from '../../../common/type';
import { formatByDataFormatType, getFormattedValue } from '../../../utils/utils';
import type { ECharts } from 'echarts';
import * as echarts from 'echarts';
import { useEffect, useRef, useContext } from 'react';
import { ColumnType } from '../../../common/type';
import { ChartItemContext } from '../../ChatItem';
import { useExportByEcharts } from '../../../hooks';
type Props = {
  data: MsgDataType;
  metricField: ColumnType;
  categoryField: ColumnType;
  triggerResize?: boolean;
  question?: string;
};

const PieChart: React.FC<Props> = ({
  data,
  question = "",
  metricField,
  categoryField,
  triggerResize,
}) => {
  const chartRef = useRef<any>();
  const instanceRef = useRef<ECharts>();
  const { downloadChartAsImage } = useExportByEcharts({
    instanceRef,
    question,
  });
  const { queryResults } = data;
  const categoryColumnName = categoryField?.bizName || '';
  const metricColumnName = metricField?.bizName || '';
  const { register } = useContext(ChartItemContext);

  const renderChart = () => {
    let instanceObj: any;
    if (!instanceRef.current) {
      instanceObj = echarts.init(chartRef.current);
      instanceRef.current = instanceObj;
    } else {
      instanceObj = instanceRef.current;
    }

    const data = queryResults || [];
    const seriesData = data.map((item, index) => {
      const value = item[metricColumnName];
      let name;
      if (item.hasOwnProperty(categoryColumnName)) {
        const categoryName = item[categoryColumnName];
        if (categoryName === undefined || categoryName === null) {
          name = '未知';
        } else if (typeof categoryName === 'string' && categoryName.trim() === '') {
          name = '未知';
        } else {
          name = String(categoryName);
        }
      } else {
        // 字段不存在的情况
        name = `未知`;
      }
      return {
        name,
        value,
        itemStyle: {
          color: THEME_COLOR_LIST[index % THEME_COLOR_LIST.length],
        },
      };
    });

    instanceObj.setOption({
      tooltip: {
        trigger: 'item',
        formatter: function (params: any) {
          const value = params.value;
          return `${params.name}: ${metricField.dataFormatType === 'percent'
            ? formatByDataFormatType(value, metricField.dataFormatType, metricField.dataFormat)
            : getFormattedValue(value)
            }`;
        },
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        type: 'scroll',
        data: seriesData.map(item => item.name),
        selectedMode: true,
        textStyle: {
          color: '#666',
        },
      },
      series: [
        {
          name: '占比',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            position: 'outside',
            formatter: function (params: any) {
              const value = params.value;
              return `${params.name}\n${metricField.dataFormatType === 'percent'
                ? formatByDataFormatType(value, metricField.dataFormatType, metricField.dataFormat)
                : getFormattedValue(value)
                }`;
            },
            fontSize: 12,
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '14',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: true,
          },
          data: seriesData,
          minAngle: 2
        },
      ],
      graphic: {
        elements: [{
          type: 'text',
          right: 10,
          bottom: 5,
          z: 100,
          style: {
            text: '内容由AI生成',
            fontSize: 12,
            fontWeight: 'normal',
            fill: 'rgba(0, 0, 0, 0.45)'
          }
        }]
      },
    });
    instanceObj.resize();
  };

  useEffect(() => {
    if (queryResults && queryResults.length > 0) {
      renderChart();
    }
  }, [queryResults, metricField, categoryField]);

  useEffect(() => {
    if (triggerResize && instanceRef.current) {
      instanceRef.current.resize();
    }
  }, [triggerResize]);
  register('downloadChartAsImage', downloadChartAsImage);
  return <div className={`${PREFIX_CLS}-pie-chart`} ref={chartRef} />;
};

export default PieChart;
